import React, { FC } from 'react'
import TableRow from 'uiKit/table/TableRow'
import { BulkInviteValidationError } from '../../model/bulkInviteTypes'
import {
  formatBulkInviteCellValue,
  isBulkInviteCellEmpty,
} from '../../helpers/bulkInviteValidationHelpers'
import classes from './BulkInviteErrorsTable.module.scss'

const MAX_VISIBLE_ERROR_ROWS = 10
const ERROR_TABLE_ROW_HEIGHT = 57
const ERROR_TABLE_HEADER_HEIGHT = 48

const COLUMN_WIDTHS = {
  email: 160,
  firstName: 120,
  lastName: 120,
  role: 110,
  managerEmail: 120,
  issues: 170,
} as const

const ERROR_TABLE_MIN_WIDTH = Object.values(COLUMN_WIDTHS).reduce((sum, width) => sum + width, 0)

const ERROR_TABLE_COLUMNS: Array<{
  key: keyof typeof COLUMN_WIDTHS
  label: string
  sticky?: boolean
}> = [
  { key: 'email', label: 'Email' },
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'role', label: 'Role-Carer or Manager' },
  { key: 'managerEmail', label: 'Manager email address' },
  { key: 'issues', label: 'Issues', sticky: true },
]

const ROW_BG = '#FFFFFF'

interface Props {
  errors: BulkInviteValidationError[]
  emptyCellClassName: string
  issueCellClassName: string
}

const renderCellValue = (value: string | null | undefined, emptyCellClassName: string) => {
  if (isBulkInviteCellEmpty(value)) {
    return <span className={emptyCellClassName}>Empty</span>
  }

  return formatBulkInviteCellValue(value)
}

const getErrorCellValue = (
  error: BulkInviteValidationError,
  key: typeof ERROR_TABLE_COLUMNS[number]['key'],
  emptyCellClassName: string,
  issueCellClassName: string
) => {
  switch (key) {
    case 'email':
      return renderCellValue(error.email, emptyCellClassName)
    case 'firstName':
      return renderCellValue(error.firstName, emptyCellClassName)
    case 'lastName':
      return renderCellValue(error.lastName, emptyCellClassName)
    case 'role':
      return renderCellValue(error.role, emptyCellClassName)
    case 'managerEmail':
      return renderCellValue(error.managerEmail, emptyCellClassName)
    case 'issues':
      return <span className={issueCellClassName}>{error.issues.join(', ')}</span>
    default:
      return null
  }
}

const getColumnStyle = (key: keyof typeof COLUMN_WIDTHS) => ({
  width: COLUMN_WIDTHS[key],
  minWidth: COLUMN_WIDTHS[key],
  maxWidth: COLUMN_WIDTHS[key],
})

export const BulkInviteErrorsTable: FC<Props> = ({
  errors,
  emptyCellClassName,
  issueCellClassName,
}) => {
  return (
    <div
      className={classes.tableScroll}
      style={{ maxHeight: ERROR_TABLE_HEADER_HEIGHT + MAX_VISIBLE_ERROR_ROWS * ERROR_TABLE_ROW_HEIGHT }}
    >
      <div className={classes.tableInner} style={{ minWidth: ERROR_TABLE_MIN_WIDTH }}>
        <div className={classes.tableHeader}>
          {ERROR_TABLE_COLUMNS.map(column => (
            <div
              key={column.key}
              className={column.sticky ? classes.headerCellSticky : classes.headerCell}
              style={getColumnStyle(column.key)}
            >
              {column.label}
            </div>
          ))}
        </div>

        {errors.map((error, index) => (
          <TableRow
            key={`${error.rowNumber}-${index}`}
            index={index}
            style={{
              minHeight: ERROR_TABLE_ROW_HEIGHT,
              padding: '16px 20px',
              background: ROW_BG,
              backgroundColor: ROW_BG,
              justifyContent: 'flex-start',
            }}
          >
            {ERROR_TABLE_COLUMNS.map(column => (
              <div
                key={column.key}
                className={column.sticky ? classes.bodyCellSticky : classes.bodyCell}
                style={getColumnStyle(column.key)}
              >
                <div className={column.key === 'issues' ? classes.cellContent : `${classes.cellContent} ${classes.cellContentTruncate}`}>
                  {getErrorCellValue(error, column.key, emptyCellClassName, issueCellClassName)}
                </div>
              </div>
            ))}
          </TableRow>
        ))}
      </div>
    </div>
  )
}
